import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon, HidePassword, VectorIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IResetPassordUser, IUser } from '~/utils/interface/user';
import userValidation from '~/validation/user';
import { RoleIdUserEnum } from '~/utils/enum';
import userApi from '~/apis/user';

type ResetPasswordUserDialogProps = {
  open: boolean;
  data?: IUser;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IResetPassordUser = {
  password: '',
};

const ResetPasswordUserDialog = (props: ResetPasswordUserDialogProps) => {
  const { t } = useTranslation('admin/user');

  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: userValidation(t).resetPassword,
    initialValues,
    onSubmit: async (values) => {
      try {
        const username: string = cast(props?.data?.username);
        if (!username) return;

        await userApi.resetPassword({
          username: username,
          password: values.password,
        });

        notify('success', t('message.success.reset_password', { username: username }));
        handleSetOpen(false, false);
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const getRoleId = (roleId?: number, provinceName?: string, wardName?: string) => {
    switch (roleId) {
      case RoleIdUserEnum.QUAN_TRI:
        return t('roleid.' + RoleIdUserEnum.QUAN_TRI);
      case RoleIdUserEnum.TINH_THANH:
        return `${t('roleid.' + RoleIdUserEnum.TINH_THANH)} ${provinceName ? `- ${provinceName}` : ''}`;
      case RoleIdUserEnum.XA_PHUONG:
        return `${t('roleid.' + RoleIdUserEnum.XA_PHUONG)} ${wardName ? `- ${wardName}` : ''}`;
      case RoleIdUserEnum.NGUOI_DEO:
        return t('roleid.' + RoleIdUserEnum.NGUOI_DEO);
      case RoleIdUserEnum.TRUNG_UONG:
        return t('roleid.' + RoleIdUserEnum.TRUNG_UONG);
      default:
        return '';
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {t('message.title.reset_password')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component='form'>
          <BaseRow className='mb-3 gap-3'>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('name2')}</InputLabel>
              <TextField
                className='w-full rounded-xl'
                placeholder={t('placeholder.name')}
                value={props.data?.name ?? ''}
                disabled
              />
            </Box>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('roleid.title')}</InputLabel>
              <TextField
                className='w-full rounded-xl'
                placeholder={t('placeholder.roleid')}
                value={getRoleId(props.data?.roleid, props.data?.province_name, props.data?.ward_name) ?? ''}
                disabled
              />
            </Box>
          </BaseRow>
          <Typography className='text-[#161A1F] font-medium text-base mb-2'>{t('user_info')}</Typography>
          <Box className='mb-3'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('nameuser')}</InputLabel>
            <TextField className='w-full rounded-xl' value={props.data?.username ?? ''} disabled />
          </Box>
          <Box className='mb-3'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('password')}</InputLabel>
            <TextField
              name='password'
              autoComplete='off'
              className='w-full rounded-xl'
              placeholder={t('placeholder.password')}
              type={showNewPassword ? 'text' : 'password'}
              value={form.values.password}
              onChange={form.handleChange}
              error={Boolean(form.errors?.password)}
              helperText={form.errors?.password}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label={showNewPassword ? 'hide password' : 'show password'}
                      onClick={() => {
                        setShowNewPassword(!showNewPassword);
                      }}
                      edge='end'
                    >
                      {showNewPassword ? <VectorIcon /> : <HidePassword />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                  '& input::-ms-reveal, & input::-ms-clear': {
                    display: 'none',
                  },
                },
              }}
            />
          </Box>
          <Box className='flex justify-between gap-3'>
            <Button
              variant='outlined'
              className='w-full bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
              onClick={() => handleSetOpen(false)}
            >
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              variant='contained'
              className='w-full bg-[#123B6D] text-base font-semibold'
              onClick={() => form.handleSubmit()}
            >
              {t('saveChange', { ns: 'admin/common' })}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ResetPasswordUserDialog);
